import React, { useEffect, useRef, useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import clsx from "clsx";
import dayjs from "dayjs";
import { Badge, Button, Select, Tag } from "antd";
import { HomeOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router";
import Table from "../../Common/Table";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useBreadcrumbs } from "../../../context/breadcrumbs";
import { ActionType, RequestData } from "@ant-design/pro-components";
import { SortOrder } from "antd/es/table/interface";
import {
  useFieldTechniciansTableGet,
  FieldTechnicianTableRow,
  FieldTechniciansGetParams,
  TechnicianStatus,
  COPSStatusDisplay,
} from "../../../hooks/api/fieldTechnicians";
import { queryFilterParams, getSorterParams } from "../../../utils";
import { UserStatusDisplay } from "../../../enums/user";
import { UserStatusType } from "../../../store/auth";
import { dateTimeFormat } from "../../../contstant";

export enum UserStatusColor {
  "active" = "green",
  "inactive" = "gray",
}

export const TechniciansStatusLabels: Record<TechnicianStatus, string> = {
  [TechnicianStatus.ACTIVE]: "active",
  [TechnicianStatus.INACTIVE]: "inactive",
};

export function getUserStatusColor(status: UserStatusType): string {
  // @ts-ignore take a color from the list
  return UserStatusColor[status?.toLowerCase()] || "rgba(0,0,0,.15)";
}

const columns = (t: TFunction): ProColumns<FieldTechnicianTableRow>[] => [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    sorter: true,
    hideInSearch: true,
    renderText: (_, record) => (
      <span>
        {record.firstName} {record.lastName}
      </span>
    ),
  },
  {
    title: t("email"),
    dataIndex: "email",
    key: "email",
    hideInSearch: true,
    sorter: true,
  },
  {
    title: "Phone number",
    dataIndex: "phone",
    key: "phone",
    hideInSearch: true,
    sorter: true,
  },
  {
    title: "COPS match",
    dataIndex: "COPSMatch",
    key: "COPSMatch",
    sorter: true,
    valueEnum: COPSStatusDisplay,
  },
  {
    title: t("common.table.createdAt"),
    dataIndex: "createdAt",
    key: "createdAt",
    hideInSearch: true,
    sorter: true,
    renderText: (text) => dayjs(text).format(dateTimeFormat),
  },
  {
    title: "Last activity",
    dataIndex: "lastLoginAt",
    key: "lastLoginAt",
    hideInSearch: true,
    sorter: true,
    renderText: (text) => {
      if (!text || text === "null" || text === "undefined") return "-";
      const date = dayjs(text);
      if (!date.isValid()) return "-";

      return date.format(dateTimeFormat);
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    sorter: true,
    valueEnum: UserStatusDisplay,
    render: (_, record) => (
      <Badge
        color={getUserStatusColor(record.status as UserStatusType)}
        text={<span className="capitalize-first-letter">{record.status}</span>}
      />
    ),
    width: 120,
  },
  {
    title: "Last activity",
    dataIndex: "lastLoginAt",
    key: "lastLoginAt",
    sorter: true,
    valueType: "dateRange",
    hideInTable: true,
    search: {
      transform: (value?: [string, string]) => {
        if (!value || value.length !== 2) return {};

        return {
          lastLoginFrom: value[0],
          lastLoginTo: value[1],
        };
      },
    },
  },
];

function ToolBar() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return [
    // <Button
    //   key="add"
    //   icon={<PlusCircleOutlined />}
    //   type="primary"
    //   onClick={(e) => {
    //     e.preventDefault();
    //     navigate("create");
    //   }}
    // >
    //   {t("common.table.add")}
    // </Button>,
  ];
}

export default function FieldTechniciansContent(): React.ReactNode | null {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { t, i18n } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const fieldTechniciansGet = useFieldTechniciansTableGet();
  const actionRef = useRef<ActionType>(null);

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: "Field Technicians" },
    ]);
  }, [i18n.language, t]);

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number;
      current?: number;
      keyword?: string;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<FieldTechnicianTableRow>>> => {
    const newParams: FieldTechniciansGetParams = {
      page: current || 1,
      pageSize: pageSize || 20,
      ...args,
      ...getSorterParams(sorter),
    };

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return fieldTechniciansGet.fetch(newParams).then((response) => {
      if (response) {
        const { data, total } = response;

        return {
          data: data || [],
          success: true,
          total,
        };
      }

      return { data: [], success: false, total: 0 };
    });
  };

  return (
    <Table<FieldTechnicianTableRow>
      key={`${tableKey}-${i18n.language}`}
      resetKey={() => setTableKey(Date.now())}
      className={clsx("transparent")}
      headerTitle="List of Field Technicians"
      toolBarRender={ToolBar}
      request={tableRequest}
      columns={columns(t)}
      actionRef={actionRef}
      actions={[
        [
          "edit",
          ({ record }) => ({
            title: "Edit technician",
            onClick: (e: MouseEvent) => {
              e.preventDefault();
              const currentPage = actionRef.current?.pageInfo?.current;
              navigate(`${record.id}/edit`, {
                state: { fromPage: currentPage?.toString() },
              });
            },
          }),
        ],
      ]}
      onReset={() => {
        setSearchParams({}, { replace: true });
        setTableKey(Date.now());
      }}
      search={{
        style: {
          // display: "none",
        },
      }}
      options={{
        search: {
          name: "search",
          placeholder: "Search",
          style: { width: 296 },
        },
      }}
      columnsState={{
        persistenceKey: "pro-table-field-technicians",
        persistenceType: "localStorage",
      }}
    />
  );
}
